import { motion } from "framer-motion";
import { Link, useOutletContext } from "react-router-dom";
import { Building2, Handshake, MonitorPlay, Radio, Trophy, Users } from "lucide-react";
import Seo from "@/components/site/Seo";
import { Cta, DemoBadge, Reveal, SectionHeading, StatusPill } from "@/components/site/kit";
import { NewsCard, TeamCard } from "@/components/site/cards";
import useFetch from "@/hooks/useFetch";
import { SPONSOR_TIERS } from "@/lib/site";

const STATS = [
  { v: "12", l: "Planned Franchises" },
  { v: "2", l: "Host Cities" },
  { v: "T10", l: "Match Format" },
  { v: "1", l: "Regional League" },
];

const Hero = () => (
  <section data-testid="home-hero" className="grain relative flex min-h-[100svh] items-end overflow-hidden pb-20 pt-40">
    <img src="/images/hero.jpg" alt="Floodlit cricket stadium packed with fans at night" fetchpriority="high" className="absolute inset-0 h-full w-full scale-105 object-cover" />
    <div className="absolute inset-0 bg-[#0A192F]/70" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F] via-transparent to-[#0A192F]/60" />
    <div className="absolute -left-24 bottom-0 h-[2px] w-[140%] -rotate-6 bg-gradient-to-r from-transparent via-[#00E5FF] to-transparent opacity-60" />
    <div className="container-x relative">
      <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="eyebrow mb-6">
        Telangana · Andhra Pradesh · Tennis-ball Cricket
      </motion.p>
      <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.7 }} className="max-w-5xl font-display text-5xl uppercase leading-[0.9] sm:text-6xl lg:text-8xl">
        Two States. <span className="text-[#00E5FF]">Twelve Franchises.</span> One T10 <span className="text-[#FF5722]">Revolution.</span>
      </motion.h1>
      <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="mt-7 max-w-2xl text-base text-slate-200 md:text-lg">
        A new franchise-based tennis-ball cricket league for Telangana and Andhra Pradesh — built for speed, local pride and the next generation of cricketing talent.
      </motion.p>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }} className="mt-10 flex flex-wrap gap-4">
        <Cta to="/about" testid="hero-explore-btn">Explore the League</Cta>
        <Cta to="/players/register" variant="accent" testid="hero-register-btn">Register as a Player</Cta>
      </motion.div>
    </div>
  </section>
);

const Intro = () => (
  <section data-testid="home-intro" className="container-x grid gap-12 py-24 lg:grid-cols-[1.1fr_1fr] lg:items-center">
    <Reveal>
      <SectionHeading eyebrow="The League" title="Where Regional Cricket Meets a New Era" />
      <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 md:text-lg">
        The Telangana–Andhra T10 League is being developed by CGN Sports as a franchise-based T10 tennis-ball cricket competition. Planned across Hyderabad and Amaravati, the league aims to create opportunities for players, franchises, sponsors and cricket fans.
      </p>
      <div className="mt-8"><Cta to="/about" variant="ghost" testid="intro-about-btn">About the League</Cta></div>
    </Reveal>
    <Reveal delay={0.15} className="relative">
      <img src="/images/street-cricket.jpg" alt="Tennis-ball cricket being played on a city street in India" loading="lazy" className="aspect-[4/5] w-full rounded-md object-cover sm:aspect-[4/3] lg:aspect-[4/5]" />
      <div className="absolute -bottom-6 -left-4 rounded-md border border-white/10 bg-[#0A192F]/90 p-5 backdrop-blur-xl sm:-left-8">
        <p className="font-score text-xs uppercase tracking-widest text-slate-400">Format</p>
        <p className="font-display text-4xl text-[#00E5FF]">10 OVERS</p>
        <p className="text-xs text-slate-400">per side · tennis ball</p>
      </div>
    </Reveal>
  </section>
);

const Stats = () => (
  <section data-testid="home-stats" className="border-y border-white/10 bg-[#071325]">
    <div className="container-x grid grid-cols-2 lg:grid-cols-4">
      {STATS.map((s, i) => (
        <Reveal key={s.l} delay={i * 0.08} className={`border-white/10 px-4 py-10 sm:px-8 ${i % 2 ? "" : "border-r"} ${i < 2 ? "border-b lg:border-b-0" : ""} lg:border-r ${i === 3 ? "lg:border-r-0" : ""}`}>
          <p data-testid={`stat-${i}`} className="font-score text-6xl font-bold text-white sm:text-7xl">{s.v}</p>
          <p className="mt-2 text-xs font-bold uppercase tracking-[0.2em] text-[#00E5FF]">{s.l}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const Franchises = () => {
  const { data: teams } = useFetch("/teams", []);
  return (
    <section data-testid="home-franchises" className="container-x py-24">
      <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <SectionHeading eyebrow="Franchise Reveal" title="12 Franchises. Two States. One League.">
          Franchise identities will be revealed as they are officially confirmed.
        </SectionHeading>
        <Cta to="/teams" variant="ghost" testid="home-all-teams-btn">All Teams</Cta>
      </div>
      <div className="mt-12 grid grid-cols-2 gap-4 sm:gap-6 md:grid-cols-3 lg:grid-cols-4">
        {(teams || []).map((t, i) => <Reveal key={t.id} delay={(i % 4) * 0.06}><TeamCard team={t} /></Reveal>)}
      </div>
    </section>
  );
};

const CITIES = [
  { slug: "hyderabad", name: "Hyderabad", state: "Telangana", img: "/images/hyderabad.jpg", alt: "Charminar monument in Hyderabad", desc: "Telangana's capital and a city with a deep-rooted cricket culture and a thriving tennis-ball cricket scene." },
  { slug: "amaravati", name: "Amaravati", state: "Andhra Pradesh", img: "/images/amaravati.jpg", alt: "Krishna river and bridge in the Amaravati region", desc: "The capital region of Andhra Pradesh on the Krishna river, home to a new generation of cricketing talent." },
];

const Cities = () => (
  <section data-testid="home-cities" className="bg-[#071325] py-24">
    <div className="container-x">
      <SectionHeading eyebrow="Host Cities" title="Two Cities. One Stage." />
      <div className="mt-12 grid gap-6 md:grid-cols-2">
        {CITIES.map((c, i) => (
          <Reveal key={c.slug} delay={i * 0.1}>
            <Link to={`/venues/${c.slug}`} data-testid={`city-card-${c.slug}`} className="group relative block aspect-[4/5] overflow-hidden rounded-md border border-white/10 sm:aspect-[16/11]">
              <img src={c.img} alt={c.alt} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F] via-[#0A192F]/50 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8">
                <StatusPill>Venue announcement coming soon</StatusPill>
                <h3 className="mt-4 font-display text-5xl uppercase leading-none">{c.name}</h3>
                <p className="mt-1 text-xs font-bold uppercase tracking-[0.25em] text-[#00E5FF]">{c.state}</p>
                <p className="mt-3 max-w-md text-sm text-slate-300">{c.desc}</p>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Split = ({ testid, eyebrow, title, body, cta, to, img, alt, icon: Icon, reverse, variant = "accent", ctaId }) => (
  <section data-testid={testid} className="container-x grid gap-10 py-24 lg:grid-cols-2 lg:items-center">
    <Reveal className={reverse ? "lg:order-2" : ""}>
      <div className="relative overflow-hidden rounded-md">
        <img src={img} alt={alt} loading="lazy" className="aspect-[16/11] w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0A192F]/70 to-transparent" />
        {Icon && <Icon className="absolute bottom-5 left-5 h-10 w-10 text-[#00E5FF]" />}
      </div>
    </Reveal>
    <Reveal delay={0.12}>
      <SectionHeading eyebrow={eyebrow} title={title}>{body}</SectionHeading>
      <div className="mt-8"><Cta to={to} variant={variant} testid={ctaId}>{cta}</Cta></div>
    </Reveal>
  </section>
);

const Sponsorship = () => (
  <section data-testid="home-sponsors" className="bg-[#071325] py-24">
    <div className="container-x">
      <SectionHeading eyebrow="Sponsors & Partnerships" title="Partner With the League">
        Sponsorship opportunities are open for brands that want to connect with cricket fans across two states.
      </SectionHeading>
      <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {SPONSOR_TIERS.map((s, i) => (
          <Reveal key={s.name} delay={i * 0.06} className={i === 0 ? "sm:col-span-2 lg:col-span-1" : ""}>
            <div data-testid={`sponsor-tier-${i}`} className="card-lift h-full rounded-md border border-white/10 bg-[#112240] p-6">
              <span className="font-score text-xs text-slate-500">0{i + 1}</span>
              <h3 className="mt-6 font-display text-2xl uppercase">{s.name}</h3>
              <p className="mt-3 text-sm text-slate-400">{s.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <div className="mt-10"><Cta to="/sponsors#enquiry" testid="home-become-partner-btn">Become a Partner</Cta></div>
    </div>
  </section>
);

const News = () => {
  const { data } = useFetch("/news?limit=3", []);
  if (!data?.length) return null;
  return (
    <section data-testid="home-news" className="container-x py-24">
      <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <div>
          <SectionHeading eyebrow="Latest News" title="From the League Desk" />
          {data.some((n) => n.is_demo) && <p className="mt-4 flex items-center gap-2 text-sm text-slate-400"><DemoBadge /> Sample articles shown until official announcements are published.</p>}
        </div>
        <Cta to="/news" variant="ghost" testid="home-all-news-btn">All News</Cta>
      </div>
      <div className="mt-12 grid gap-6 md:grid-cols-3">
        {data.map((n) => <NewsCard key={n.id} article={n} />)}
      </div>
    </section>
  );
};

export default function Home() {
  const { settings = {} } = useOutletContext() || {};
  return (
    <>
      <Seo
        description="Two States. Twelve Franchises. One T10 Revolution. The official website of the Telangana–Andhra T10 League, organised by CGN Sports."
        jsonLd={{ "@context": "https://schema.org", "@type": "SportsOrganization", name: "Telangana–Andhra T10 League", sport: "Cricket", url: window.location.origin, parentOrganization: { "@type": "Organization", name: "CGN Sports" } }}
      />
      <Hero />
      <Intro />
      <Stats />
      <Franchises />
      <Cities />
      <Split testid="home-players" eyebrow="Player Ecosystem" title="Your Game. Your Opportunity." body="Be part of a new regional platform for tennis-ball cricket talent. Register your interest and follow official announcements about eligibility, trials and player selection." cta="Register as a Player" to="/players/register" img="/images/batsman.jpg" alt="Batter playing an attacking shot" icon={Users} ctaId="home-player-register-btn" />
      <Split testid="home-franchise" eyebrow="Franchise Opportunities" title="Build Your Franchise. Be Part of the League." body="Twelve franchises are planned across Telangana and Andhra Pradesh. Submit an expression of interest to learn about the participation process." cta="Explore Franchise Opportunities" to="/franchise" img="/images/crowd.jpg" alt="Fans in a stadium at night" icon={Building2} reverse variant="primary" ctaId="home-franchise-btn" />
      <section data-testid="home-media" className="grain relative overflow-hidden border-y border-white/10 py-24">
        <img src="/images/broadcast.jpg" alt="Broadcast camera operator at a sports ground" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-[#0A192F]/70" />
        <div className="container-x relative grid gap-10 lg:grid-cols-[1.3fr_1fr] lg:items-end">
          <Reveal>
            <SectionHeading eyebrow="Broadcasting & Media" title="Taking Regional Cricket to Digital Audiences">
              CGN Sports is exploring broadcasting and OTT distribution opportunities for the league.
            </SectionHeading>
            <p className="mt-4 text-xs uppercase tracking-widest text-slate-400">No broadcasting partner has been confirmed.</p>
            <div className="mt-8"><Cta to="/media#enquiry" testid="home-media-enquiry-btn">Media & Broadcasting Enquiry</Cta></div>
          </Reveal>
          <div className="grid grid-cols-3 gap-3">
            {[{ i: MonitorPlay, l: "OTT" }, { i: Radio, l: "Broadcast" }, { i: Handshake, l: "Media Rights" }].map(({ i: I, l }) => (
              <div key={l} className="rounded-md border border-white/10 bg-[#0A192F]/80 p-5 backdrop-blur-xl">
                <I className="h-6 w-6 text-[#00E5FF]" />
                <p className="mt-4 text-xs font-bold uppercase tracking-widest">{l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {settings.show_sponsors_on_home !== false && <Sponsorship />}
      {settings.show_news_on_home !== false && <News />}
      <section className="container-x pb-24">
        <div className="beam flex flex-col items-start justify-between gap-6 rounded-md bg-[#112240] p-8 sm:p-12 md:flex-row md:items-center">
          <div className="flex items-center gap-4">
            <Trophy className="h-10 w-10 shrink-0 text-[#FF5722]" />
            <p className="font-display text-2xl uppercase sm:text-3xl">Official announcements will be published here first.</p>
          </div>
          <Cta to="/contact" variant="ghost" testid="home-contact-btn">Contact the League</Cta>
        </div>
      </section>
    </>
  );
}
